import { useEffect, useRef, useState } from "react";

import { ExclamationTriangleIcon, XCircleIcon } from "@heroicons/react/24/outline";

import useWasteTracking from "../../data/useWasteTracking";
import Modal from "../../layouts/Modal";
import { IWasteReceiptSubmitResult } from "../../models/wasteTracking/IWasteReceiptRow";
import { chunkIds, IWasteReceiptSubmitSummary, summariseSubmitResults } from "../../utils/wasteTracking";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";

interface WasteReceiptSubmitModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Resolves the ids to send once the user confirms - selected rows, or "all ready" from the API. */
  resolveVehicleIds: () => Promise<string[]>;
  title: string;
  description: string;
}

type Phase = "confirm" | "resolving" | "sending" | "done";

// Drives a chunked submission: 25 vehicles per API call, sequentially, with a progress bar and a
// results list at the end. Sequential on purpose - DEFRA rate-limits, and one call per chunk
// means a network fault only ever loses the chunk in flight (the API saves per vehicle anyway).
export default function WasteReceiptSubmitModal({ open, setOpen, resolveVehicleIds, title, description }: WasteReceiptSubmitModalProps) {
  const { submit, invalidate } = useWasteTracking("WasteReceiptSubmitModal");
  const { addNotification } = useNotification();

  const [phase, setPhase] = useState<Phase>("confirm");
  const [total, setTotal] = useState(0);
  const [done, setDone] = useState(0);
  const [summary, setSummary] = useState<IWasteReceiptSubmitSummary | null>(null);
  const [fault, setFault] = useState<string | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    if (open) {
      setPhase("confirm");
      setTotal(0);
      setDone(0);
      setSummary(null);
      setFault(null);
      cancelled.current = false;
    }
  }, [open]);

  const run = async () => {
    setPhase("resolving");

    let ids: string[];
    try {
      ids = await resolveVehicleIds();
    } catch (err) {
      setFault(err instanceof Error ? err.message : "Could not work out which vehicles to submit");
      setPhase("done");
      return;
    }

    if (ids.length === 0) {
      addNotification({ variant: "info", primaryText: "Nothing ready to submit" });
      setOpen(false);
      return;
    }

    setTotal(ids.length);
    setPhase("sending");

    const results: IWasteReceiptSubmitResult[] = [];

    for (const chunk of chunkIds(ids)) {
      if (cancelled.current) break;

      try {
        const response = await submit.mutateAsync(chunk);
        results.push(...(response.data ?? []));
      } catch (err) {
        // The chunk in flight is unknown; anything already saved server-side will show on refresh.
        setFault(err instanceof Error ? err.message : "Submission failed part way through");
        break;
      }

      setDone((d) => d + chunk.length);
    }

    invalidate();
    setSummary(summariseSubmitResults(results));
    setPhase("done");
  };

  const close = () => {
    cancelled.current = true;
    setOpen(false);
  };

  const percent = total === 0 ? 0 : Math.round((done / total) * 100);

  return (
    <Modal width="max-w-3xl" open={open} setOpen={phase === "sending" ? () => {} : setOpen}>
      <h1 className="text-xl">{title}</h1>
      <p className="text-sm text-gray-500">{description}</p>

      {phase === "confirm" && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-gray-700">
            Each vehicle is sent to DEFRA as an End-of-life vehicle (EWC 16 01 04) movement. Rows that are not ready are skipped and
            listed, not sent. Successful rows move to the Submitted tab with their waste tracking ID.
          </p>
          <div className="flex justify-end gap-2">
            <ButtonInput label="Cancel" isSubmit={false} onClick={close} classes="bg-gray-500 hover:bg-gray-600" />
            <ButtonInput label="Submit to DEFRA" isSubmit={false} onClick={run} classes={undefined} />
          </div>
        </div>
      )}

      {(phase === "resolving" || phase === "sending") && (
        <div className="mt-6 space-y-3">
          {phase === "resolving" ? (
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <LoadingWheel />
              Working out which vehicles are ready…
            </div>
          ) : (
            <>
              <div className="flex justify-between text-sm text-gray-700">
                <span>Submitting to DEFRA…</span>
                <span>
                  {done} / {total}
                </span>
              </div>
              <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
                <div className="h-3 rounded-full bg-primary-600 transition-all" style={{ width: `${percent}%` }} />
              </div>
              <p className="text-xs text-gray-500">Keep this window open until it finishes.</p>
            </>
          )}
        </div>
      )}

      {phase === "done" && (
        <div className="mt-4 space-y-4">
          {fault && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <XCircleIcon className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="font-medium">Stopped early</p>
                <p>{fault}</p>
                <p className="mt-1 text-xs">Anything DEFRA accepted before the fault is already saved; refresh the table and resubmit the rest.</p>
              </div>
            </div>
          )}

          {summary && (
            <>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-md bg-green-50 p-3">
                  <p className="text-2xl font-semibold text-green-700">{summary.submitted}</p>
                  <p className="text-xs text-green-800">Submitted</p>
                </div>
                <div className="rounded-md bg-red-50 p-3">
                  <p className="text-2xl font-semibold text-red-700">{summary.failed}</p>
                  <p className="text-xs text-red-800">Rejected by DEFRA</p>
                </div>
                <div className="rounded-md bg-yellow-50 p-3">
                  <p className="text-2xl font-semibold text-yellow-700">{summary.skipped}</p>
                  <p className="text-xs text-yellow-800">Skipped (not ready)</p>
                </div>
              </div>

              {summary.problems.length > 0 && (
                <div className="max-h-72 overflow-y-auto divide-y rounded-md border border-gray-200">
                  {summary.problems.map((problem) => (
                    <div key={problem.vehicleId} className="flex items-start gap-2 p-3 text-sm">
                      {problem.skipped ? (
                        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-yellow-500" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 flex-shrink-0 text-red-500" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{problem.vrm}</p>
                        <ul className="list-disc pl-4 text-gray-700">
                          {problem.messages.map((message, i) => (
                            <li key={i}>{message}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="flex justify-end">
            <ButtonInput label="Close" isSubmit={false} onClick={close} classes={undefined} />
          </div>
        </div>
      )}
    </Modal>
  );
}
